import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Paper
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import imageCompression from "browser-image-compression";
import axios from "axios";

function OriginalPhotos({ eventName, userId }) {
  const [images, setImages] = useState([]);
  const [imageDialog, setImageDialog] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [addDialog, setAddDialog] = useState(false);
  const [topMenuAnchor, setTopMenuAnchor] = useState(null);
  const [folders, setFolders] = useState(["Original Photos", "Copy Photos", "Event Copy"]);
  const [createFolderDialog, setCreateFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const fileInputRef = useRef(null);

  const clientMongoId = localStorage.getItem("clientMongoId");

  // Fetch images from DB on mount
  useEffect(() => {
    async function fetchImages() {
      const res = await axios.get(
        `https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${userId}/images?folder=Original%20Photos`
      );
      setImages(res.data); // Array of { folder, src }
    }
    fetchImages();
  }, [userId]);

  // Add image handler (open file dialog)
  const handleAddImage = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // Handle file selection and compress/upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("folder", "Original Photos");
      // Send to backend (multipart/form-data)
      const res = await axios.post(
        `https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${userId}/images`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setImages(res.data); // Update images from backend response
    }
  };

  // Image click handler
  const handleImageClick = (img) => {
    setCurrentImage(img);
    setImageDialog(true);
  };

  // Like handler
  const handleLike = (idx) => {
    setImages(prev =>
      prev.map((img, i) =>
        i === idx ? { ...img, liked: !img.liked } : img
      )
    );
  };

  // Select handler
  const handleSelect = (idx) => {
    setImages(prev =>
      prev.map((img, i) =>
        i === idx ? { ...img, selected: !img.selected } : img
      )
    );
  };

  // Copy to folder handler
  const handleCopyToFolder = async (targetFolder) => {
    setTopMenuAnchor(null);
    const selectedImages = images.filter(img => img.selected);
    for (const img of selectedImages) {
      await axios.post(
        `https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${userId}/images`,
        { folder: targetFolder, image: img.src }
      );
    }
    alert(`Selected images copied to ${targetFolder} folder!`);
  };

  // Delete image handler
  const handleDelete = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  // Delete selected images handler
  const handleDeleteSelected = async () => {
    // Find selected image indexes
    const selectedIndexes = images
      .map((img, idx) => img.selected ? idx : null)
      .filter(idx => idx !== null);

    for (const idx of selectedIndexes.reverse()) {
      await axios.delete(
        `https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${userId}/images/${idx}?folder=Original Photos`
      );
    }
    // Refetch images after deletion
    const res = await axios.get(`https://studio-lc.onrender.com/api/users/${userId}/images?folder=Original%20Photos`);
    setImages(res.data);
  };

  const handleTopMenuOpen = (event) => {
    setTopMenuAnchor(event.currentTarget);
  };

  const handleTopMenuClose = () => {
    setTopMenuAnchor(null);
  };

  // Create folder handler
  const handleCreateFolder = async () => {
    if (!newFolderName) return;
    setCreateFolderDialog(false);
    // Create folder on server
    await axios.post(
      `https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${userId}/folders`,
      { name: newFolderName }
    );
    setFolders(prev => [...prev, newFolderName]); // Update local folder list
    setNewFolderName("");
  };

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh", pt: 2, px: 2 }}>
      {/* Heading and Top Menu */}
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        bgcolor: "#fff",
        zIndex: 10,
        py: 2,
        borderBottom: "1px solid #eee"
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "#1976d2", ml: 1, fontFamily:"Josefin Sans", }}>
          {eventName || "Event Name"}
        </Typography>
        <IconButton onClick={handleTopMenuOpen}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={topMenuAnchor}
          open={Boolean(topMenuAnchor)}
          onClose={handleTopMenuClose}
        >
          {folders.map((folder) => (
            <MenuItem key={folder} onClick={() => handleCopyToFolder(folder)}>
              <FolderCopyIcon sx={{ mr: 1 }} /> Copy to {folder}
            </MenuItem>
          ))}
          <MenuItem onClick={handleDeleteSelected}>
            <DeleteIcon sx={{ mr: 1, color: "#d32f2f" }} /> Delete Selected
          </MenuItem>
        </Menu>
      </Box>

      {/* Add Image Button */}
      <Box sx={{ display: "flex", alignItems: "center", mt: 3, mb: 2 }}>
        <IconButton color="primary" onClick={handleAddImage}>
          <AddIcon fontSize="large" />
        </IconButton>
        <Typography sx={{ ml: 2, fontWeight: 600, fontSize: 18, fontFamily:"Josefin Sans", }}>
          Add Photo
        </Typography>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </Box>

      {/* Images Grid */}
      <Grid container spacing={3}>
        {images.map((img, idx) => (
          <Grid item xs={12} sm={6} md={4} key={idx}>
            <Paper sx={{
              p: 2,
              borderRadius: 3,
              boxShadow: 3,
              position: "relative"
            }}>
              <Box
                sx={{
                  width: "100%",
                  height: 180,
                  bgcolor: "#eee",
                  borderRadius: 2,
                  overflow: "hidden",
                  cursor: "pointer",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
                onClick={() => handleImageClick(img)}
              >
                <img
                  src={img.src}
                  alt=""
                  style={{ maxWidth: "100%", maxHeight: "100%", borderRadius: "8px" }}
                />
              </Box>
              {/* Icons below image */}
              <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 1
              }}>
                <IconButton onClick={() => handleLike(idx)}>
                  {img.liked ? <FavoriteIcon sx={{ color: "#d32f2f" }} /> : <FavoriteBorderIcon />}
                </IconButton>
                <IconButton onClick={() => handleSelect(idx)}>
                  {img.selected ? <CheckBoxIcon sx={{ color: "#1976d2" }} /> : <CheckBoxOutlineBlankIcon />}
                </IconButton>
                <IconButton onClick={async () => {
                  await axios.delete(
                    `https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${userId}/images/${idx}?folder=Original Photos`
                  );
                  setImages(images.filter((_, i) => i !== idx));
                }}>
                  <DeleteIcon sx={{ color: "#d32f2f" }} />
                </IconButton>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Full Screen Image Dialog */}
      <Dialog open={imageDialog} onClose={() => setImageDialog(false)} maxWidth="md">
        <DialogContent sx={{ bgcolor: "#000", display: "flex", justifyContent: "center", alignItems: "center" }}>
          {currentImage && (
            <img
              src={currentImage.src}
              alt=""
              style={{ maxWidth: "100%", maxHeight: "80vh", borderRadius: "12px" }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog open={createFolderDialog} onClose={() => setCreateFolderDialog(false)}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Folder Name"
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleCreateFolder} color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OriginalPhotos;